import { z } from 'zod';

import { exportFormatSchema } from '../domain/types';

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const name = z.string().trim().min(2).max(120);
/**
 * Money in the account's currency; the API rounds to cents (COP has none, but the model is
 * currency-agnostic).
 */
const money = z.number().min(0).max(1_000_000_000);
const periodLabel = z
  .string()
  .trim()
  .regex(/^\d{4}(-\d{2})?$/, 'YYYY or YYYY-MM');
const monthLabel = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM');

/**
 * Kinds of fee concept a Colombian school bills (scoping 3.10).
 */
export const feeConceptCodeSchema = z.enum([
  'matricula',
  'pension',
  'transporte',
  'alimentacion',
  'otro',
]);

/**
 * A fee concept of one school year.
 */
export const createFeeConceptInputSchema = z.object({
  schoolYearId: uuid,
  code: feeConceptCodeSchema,
  name,
  recurring: z.boolean().default(false),
  position: z.number().int().min(1).max(50).optional(),
});
export const updateFeeConceptInputSchema = z
  .object({
    name,
    recurring: z.boolean(),
    active: z.boolean(),
    position: z.number().int().min(1).max(50),
  })
  .partial();

/**
 * Replaces the tariffs of a concept: one amount per grade, or one for every grade (`gradeId` null).
 */
export const putTariffsInputSchema = z.object({
  tariffs: z
    .array(z.object({ gradeId: uuid.nullable(), amount: money }))
    .min(1)
    .max(60),
});

/**
 * Generates the charges of a concept for a period: every active enrollment of the year (or of
 * one grade) gets one charge on its family's account, once per period label.
 */
export const generateChargesInputSchema = z.object({
  /**
   * Computes what would be created without writing anything (the preview of #70).
   */
  dryRun: z.boolean().default(false),
  feeConceptId: uuid,
  gradeId: uuid.optional(),
  dueOn: isoDate,
  periodLabel,
});

/**
 * A manual charge for one student.
 */
export const createChargeInputSchema = z.object({
  studentUserId: uuid,
  description: z.string().trim().min(2).max(200),
  amount: money.positive(),
  dueOn: isoDate,
  feeConceptId: uuid.optional(),
  periodLabel: periodLabel.optional(),
});

/**
 * Query of the charges list.
 */
export const chargesQuerySchema = z.object({
  studentId: uuid.optional(),
  guardianId: uuid.optional(),
  status: z.enum(['pending', 'partial', 'paid', 'void']).optional(),
  dueBefore: isoDate.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/**
 * A payment recorded by the school (cash, transfer) or by the gateway (#64). Without
 * `allocations`, the payment settles the oldest pending charges first.
 */
export const createPaymentInputSchema = z.object({
  guardianId: uuid,
  amount: money.positive(),
  paidOn: isoDate,
  method: z.enum(['cash', 'transfer', 'gateway']),
  reference: z.string().trim().max(120).optional(),
  receiptNumber: z.string().trim().max(60).optional(),
  allocations: z
    .array(z.object({ chargeId: uuid, amount: money.positive() }))
    .max(50)
    .optional(),
});

/**
 * An opening balance brought from the school's previous system (positive = the family owes).
 */
export const openingBalanceInputSchema = z.object({
  guardianId: uuid,
  amount: z.number().min(-1_000_000_000).max(1_000_000_000),
  asOf: isoDate,
  description: z.string().trim().max(200).optional(),
});

/**
 * A discount or scholarship for a student (data now; rules and approvals in M3).
 */
export const createDiscountInputSchema = z
  .object({
    studentUserId: uuid,
    kind: z.enum(['discount', 'scholarship']),
    percent: z.number().min(0).max(100).optional(),
    amount: money.optional(),
    feeConceptId: uuid.optional(),
    reason: z.string().trim().min(2).max(200),
    startsOn: isoDate,
    endsOn: isoDate.optional(),
  })
  .refine((value) => (value.percent === undefined) !== (value.amount === undefined), {
    message: 'Give a percent or an amount, not both',
    path: ['percent'],
  });
export const updateDiscountInputSchema = z
  .object({
    status: z.enum(['active', 'inactive']),
    endsOn: isoDate.nullable(),
    reason: z.string().trim().min(2).max(200),
  })
  .partial();

/**
 * Query of the family accounts list.
 */
export const familiesQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  withBalance: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/**
 * The billing calendar of a recurring concept (ticket #109): the months it charges, as period
 * labels (`2027-02`), and the day of the month each charge falls due (1–28, so it exists in
 * every month). Saved with `PUT /billing/concepts/{id}/calendar`.
 */
export const billingCalendarInputSchema = z.object({
  enabled: z.boolean().default(true),
  dueDay: z.number().int().min(1).max(28),
  months: z
    .array(monthLabel)
    .min(1)
    .max(24)
    .refine((months) => new Set(months).size === months.length, 'months must not repeat'),
});

/**
 * Query of `GET /billing/calendars`: the recurring concepts of a school year with their calendar.
 */
export const calendarsQuerySchema = z.object({ schoolYearId: uuid });

/**
 * Runs the calendars of one month by hand (`POST /billing/calendars/run`): the same generation
 * the monthly job does, for every enabled calendar that lists the month, or for one concept.
 */
export const runCalendarsInputSchema = z.object({
  periodLabel: monthLabel,
  feeConceptId: uuid.optional(),
  dryRun: z.boolean().default(false),
});

/**
 * How a school reminds its families (ticket #109): days before the due date the `charge.due`
 * notice goes out (0 = on the due date), and days after it the `charge.overdue` one.
 */
export const reminderSettingsInputSchema = z.object({
  reminderDaysBefore: z.number().int().min(0).max(30),
  overdueDaysAfter: z.number().int().min(1).max(30),
});

/**
 * A fee concept code.
 */
export type FeeConceptCode = z.infer<typeof feeConceptCodeSchema>;
/**
 * Body of `POST /billing/concepts`.
 */
export type CreateFeeConceptInput = z.input<typeof createFeeConceptInputSchema>;
/**
 * Body of `PATCH /billing/concepts/{id}`.
 */
export type UpdateFeeConceptInput = z.input<typeof updateFeeConceptInputSchema>;
/**
 * Body of `PUT /billing/concepts/{id}/tariffs`.
 */
export type PutTariffsInput = z.input<typeof putTariffsInputSchema>;
/**
 * Body of `POST /billing/charges/generate`.
 */
export type GenerateChargesInput = z.input<typeof generateChargesInputSchema>;
/**
 * Body of `POST /billing/charges`.
 */
export type CreateChargeInput = z.input<typeof createChargeInputSchema>;
/**
 * Body of `POST /billing/payments`.
 */
export type CreatePaymentInput = z.input<typeof createPaymentInputSchema>;
/**
 * Body of `POST /billing/opening-balances`.
 */
export type OpeningBalanceInput = z.input<typeof openingBalanceInputSchema>;
/**
 * Body of `POST /billing/discounts`.
 */
export type CreateDiscountInput = z.input<typeof createDiscountInputSchema>;
/**
 * Body of `PATCH /billing/discounts/{id}`.
 */
export type UpdateDiscountInput = z.input<typeof updateDiscountInputSchema>;

/**
 * Body of `PUT /billing/concepts/{id}/calendar`.
 */
export type BillingCalendarInput = z.input<typeof billingCalendarInputSchema>;

/**
 * Query of `GET /billing/calendars`.
 */
export type CalendarsQuery = z.infer<typeof calendarsQuerySchema>;

/**
 * Body of `POST /billing/calendars/run`.
 */
export type RunCalendarsInput = z.input<typeof runCalendarsInputSchema>;

/**
 * Body of `PUT /billing/reminder-settings`.
 */
export type ReminderSettingsInput = z.input<typeof reminderSettingsInputSchema>;

/**
 * A fee concept with its tariffs.
 */
export type FeeConceptView = {
  id: string;
  schoolYearId: string;
  code: FeeConceptCode;
  name: string;
  recurring: boolean;
  active: boolean;
  position: number;
  tariffs: { gradeId: string | null; gradeName: string | null; amount: number }[];
};

/**
 * A charge on a family's account.
 */
export type ChargeView = {
  id: string;
  guardianId: string;
  studentUserId: string | null;
  studentName: string | null;
  feeConceptId: string | null;
  description: string;
  periodLabel: string | null;
  amount: number;
  discountAmount: number;
  netAmount: number;
  paidAmount: number;
  balance: number;
  dueOn: string;
  status: 'pending' | 'partial' | 'paid' | 'void';
  source: 'generated' | 'manual' | 'import' | 'mentoria' | 'opening';
  externalRef: string | null;
};

/**
 * A payment with how it was allocated.
 */
export type PaymentView = {
  id: string;
  guardianId: string;
  amount: number;
  allocatedAmount: number;
  paidOn: string;
  method: 'cash' | 'transfer' | 'gateway' | 'opening';
  reference: string | null;
  receiptNumber: string | null;
  status: 'confirmed' | 'pending' | 'failed';
  allocations: { chargeId: string; amount: number }[];
};

/**
 * What `POST /billing/charges/generate` answers.
 */
export type GenerateChargesResult = {
  created: number;
  skipped: number;
  /**
   * With `dryRun`, one row per charge that would be created.
   */
  preview?: {
    studentUserId: string;
    studentName: string | null;
    groupName: string | null;
    amount: number;
    discountAmount: number;
  }[];
  withoutFamily: { studentUserId: string; studentName: string | null }[];
  totalAmount: number;
};

/**
 * A recurring concept of a school year with its billing calendar (ticket #109). `configured` is
 * false until the school saves one; each month carries its due date and how many charges it has.
 */
export type BillingCalendarView = {
  feeConceptId: string;
  conceptName: string;
  conceptCode: FeeConceptCode;
  schoolYearId: string;
  configured: boolean;
  enabled: boolean;
  dueDay: number | null;
  months: { periodLabel: string; dueOn: string; charges: number }[];
};

/**
 * What `POST /billing/calendars/run` answers, and what the monthly job does per school: one
 * generation result per concept whose calendar lists the month.
 */
export type CalendarRunResult = {
  periodLabel: string;
  items: (GenerateChargesResult & { feeConceptId: string; conceptName: string; dueOn: string })[];
};

/**
 * The school's reminder settings (the defaults until it saves its own).
 */
export type ReminderSettingsView = {
  reminderDaysBefore: number;
  overdueDaysAfter: number;
  isDefault: boolean;
};

/**
 * A family's account statement: what they owe, what they paid, and every line behind it.
 */
export type FamilyStatement = {
  guardian: {
    id: string;
    givenName: string | null;
    familyName: string | null;
    email: string | null;
  };
  currency: string;
  students: { id: string; givenName: string | null; familyName: string | null }[];
  totals: { charged: number; discounts: number; paid: number; credit: number; balance: number };
  charges: ChargeView[];
  payments: PaymentView[];
};

/**
 * A family account as the list shows it.
 */
export type FamilyAccountView = {
  guardianId: string;
  givenName: string | null;
  familyName: string | null;
  email: string | null;
  currency: string;
  studentsCount: number;
  balance: number;
  overdue: number;
};

/**
 * A discount or scholarship.
 */
export type DiscountView = {
  id: string;
  studentUserId: string;
  kind: 'discount' | 'scholarship';
  percent: number | null;
  amount: number | null;
  feeConceptId: string | null;
  reason: string;
  startsOn: string;
  endsOn: string | null;
  status: 'active' | 'inactive';
};

/**
 * The Wompi keys a school owner enters from the school's settings (scoping 3.10). Stored in
 * Secrets Manager, never returned: `GET /billing/gateway` answers masked values.
 */
export const gatewaySettingsInputSchema = z.object({
  publicKey: z
    .string()
    .trim()
    .regex(/^pub_(test|prod)_[A-Za-z0-9]+$/, 'pub_test_… or pub_prod_…'),
  privateKey: z
    .string()
    .trim()
    .regex(/^prv_(test|prod)_[A-Za-z0-9]+$/, 'prv_test_… or prv_prod_…'),
  eventsSecret: z
    .string()
    .trim()
    .regex(/^(test|prod)_events_[A-Za-z0-9]+$/, 'test_events_… or prod_events_…'),
  integritySecret: z
    .string()
    .trim()
    .regex(/^(test|prod)_integrity_[A-Za-z0-9]+$/, 'test_integrity_… or prod_integrity_…'),
});

/**
 * Body of `PUT /billing/gateway`.
 */
export type GatewaySettingsInput = z.input<typeof gatewaySettingsInputSchema>;

/**
 * What the school sees of its gateway: the mode and the public key, the private material masked.
 */
export type GatewaySettingsView = {
  provider: 'wompi';
  configured: boolean;
  mode: 'sandbox' | 'production' | null;
  publicKey: string | null;
  privateKeyMasked: string | null;
  eventsSecretMasked: string | null;
  integritySecretMasked: string | null;
  webhookPath: string;
};

/**
 * A checkout intent as the API answers it; `checkoutUrl` only when created (the signed URL is
 * not stored).
 */
export type GatewayIntentView = {
  id: string;
  chargeId: string;
  reference: string;
  mode: 'sandbox' | 'production';
  amount: number;
  currency: string;
  status: 'created' | 'pending' | 'approved' | 'declined' | 'voided' | 'error' | 'expired';
  transactionId: string | null;
  paymentMethod: string | null;
  statusMessage: string | null;
  expiresAt: string;
  confirmedAt: string | null;
  paymentId: string | null;
  checkoutUrl?: string;
};

/**
 * Query of `GET /billing/exports`: charges (by due date) or payments (by payment date) of a
 * period, as JSON, CSV or Excel.
 */
export const billingExportQuerySchema = z
  .object({
    kind: z.enum(['charges', 'payments']).default('charges'),
    from: isoDate,
    to: isoDate,
    format: exportFormatSchema,
  })
  .refine((query) => query.from <= query.to, 'from must not be after to');

/**
 * Query of `GET /billing/exports`.
 */
export type BillingExportQuery = z.infer<typeof billingExportQuerySchema>;
